import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { fromEvent, Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'zelis-clamp',
  templateUrl: './clamp.component.html',
  styleUrls: ['./clamp.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClampComponent implements AfterViewInit, OnChanges, OnDestroy {
  @HostBinding() class = 'zelis-dls';

  @Input() clamp = true;
  @Input() lines = 1;
  @Input() expanded = false;
  @Input() showMoreText = 'Show More';
  @Input() showLessText = 'Show Less';
  /**
   * Whether the reset styles are restored.
   * Unreset CSS restores browsers' default element styles,
   * which are reset by tailwind's preflight
   */
  @Input() unreset = false;

  public hasClamp = false;
  public isClamped = true;

  private destroy: Subject<void> = new Subject();
  private userSelectedClampState!: boolean;

  @ViewChild('bannerContent') clampedElement!: ElementRef;

  constructor(
    private window: Window,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    // Do not detect changes until explicitly detected.
    this.changeDetectorRef.detach();
  }

  ngAfterViewInit() {
    this.handleChange();
    this.subscribeToWindowResize();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes.expanded?.isFirstChange() === false ||
      changes.clamp?.isFirstChange() === false ||
      changes.lines?.isFirstChange() === false
    ) {
      this.handleChange();
    }
  }

  ngOnDestroy() {
    this.destroy.next(undefined);
  }

  public onContentChange() {
    this.handleChange();
  }

  public onShowMore(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
      this.userSelectedClampState = false;
    }
    this.hasClamp = true;
    this.isClamped = false;
    this.changeDetectorRef.detectChanges();
  }

  public onShowLess(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
      this.userSelectedClampState = true;
    }
    this.hasClamp = true;
    this.isClamped = true;
    this.changeDetectorRef.detectChanges();
  }

  private shouldClamp(): boolean {
    return (
      this.clampedElement?.nativeElement.offsetHeight <
        this.clampedElement?.nativeElement.scrollHeight ||
      this.clampedElement?.nativeElement.offsetWidth <
        this.clampedElement?.nativeElement.scrollWidth
    );
  }

  private checkIsClamped() {
    if (this.shouldClamp()) {
      this.hasClamp = true;
      this.isClamped = !this.expanded;
    } else {
      this.hasClamp = false;
      this.isClamped = false;
    }
    this.changeDetectorRef.detectChanges();
  }

  private handleChange() {
    this.hasClamp = false;
    this.isClamped = true;
    this.changeDetectorRef.detectChanges();
    this.checkIsClamped();
    this.handleExpanded();
  }

  private subscribeToWindowResize() {
    this.getWindowResize().subscribe(() => this.handleChange());
  }

  private getWindowResize(): Observable<Event> {
    return fromEvent(this.window, 'resize').pipe(
      debounceTime(250),
      takeUntil(this.destroy)
    );
  }

  private handleExpanded() {
    if (!this.clamp || !this.hasClamp) {
      return;
    }
    const expanded =
      this.userSelectedClampState !== undefined
        ? !this.userSelectedClampState
        : this.expanded;
    switch (expanded) {
      case true:
        this.onShowMore();
        break;
      case false:
        this.onShowLess();
        break;
    }
  }
}
